import type { IdentifiersType } from '~/types/clarity'

// Make window optional for server-side rendering
declare const window: Window | undefined

/**
 * Checks if Clarity is available.
 *
 * The `(typeof window.clarity === 'function')` check is there to make sure that
 * WebViews don't encounter errors. For some reason they load the Clarity script
 * differently.
 *
 * @returns True if Clarity is available, false otherwise.
 */
function isClarityAvailable() {
  return typeof window?.clarity === 'function'
}

/**
 * Sends an event to Clarity.
 *
 * @param event The event to send.
 */
export function SendEventToClarity(event: string) {
  if (!isClarityAvailable()) {
    return
  }

  log('SendEventToClarity', { event })

  window?.clarity?.('event', event)
}

/**
 * Sends a tag to Clarity.
 *
 * @param tagName The name of the tag.
 * @param tagValue The value of the tag.
 */
export function SendTagToClarity(tagName: string, tagValue: string) {
  if (!isClarityAvailable()) {
    return
  }

  log('SendTagToClarity', { tagName, tagValue })

  window?.clarity?.('set', tagName, tagValue)
}

/**
 * Sends a consent to Clarity.
 */
export function SendConsentToClarity() {
  if (!isClarityAvailable()) {
    return
  }

  log('SendConsentToClarity')

  window?.clarity?.('consent')
}

/**
 * Sends identifiers to Clarity.
 *
 * @param args The identifiers to send.
 */
export function SendIdentifiersToClarity(args: IdentifiersType) {
  if (!isClarityAvailable()) {
    return
  }

  log('SendIdentifiersToClarity', { args })

  window?.clarity?.('identify', args.customId, args.customSessionId, args.customPageId, args.friendlyName)
}

/**
 * Updates the clarity screenshot.
 *
 * @param pageName The name of the page.
 */
export function UpdateClarityScreenShot(pageName?: string) {
  if (!isClarityAvailable()) {
    return
  }

  log('UpdateClarityScreenShot', { pageName })

  window?.clarity?.('set', 'page', pageName)
}
